import React from 'react';
import StickyNote, { StickyNoteEditor } from '../../../components/StickyNote';
import useStickyNote from '../api/useStickyNote';
import { LeadDetailsResponse } from '../types';
import { formatDateTimeLong } from '../utils/formatDateTime';

interface StickyNotePanelProps {
    leadId: string;
    lead?: LeadDetailsResponse;
    canEdit?: boolean;
}

// The lead's single sticky note, read from `leadDetails.latest_note`. Shares
// its save/delete routing with the list's NoteModal via `useStickyNote`.
const StickyNotePanel: React.FC<StickyNotePanelProps> = ({ leadId, lead, canEdit = true }) => {
    const latestNote = lead?.latest_note ?? null;
    const [isEditing, setIsEditing] = React.useState(false);

    const { draft, setDraft, canSave, isSaving, isPending, save, remove } = useStickyNote({
        leadId,
        note: latestNote,
        onSaved: () => setIsEditing(false)
    });

    const startEditing = () => {
        setDraft(latestNote?.body ?? '');
        setIsEditing(true);
    };

    if (!isEditing && !latestNote && !canEdit) return null;

    return (
        <StickyNote
            title={latestNote ? `Sticky note • ${formatDateTimeLong(latestNote.updated_at)}` : ''}
            name="Sticky note"
            body={latestNote?.body ?? null}
            isEditing={isEditing}
            onEdit={startEditing}
            editor={
                <StickyNoteEditor
                    name="Sticky note"
                    draft={draft}
                    onDraftChange={setDraft}
                    onCancel={() => setIsEditing(false)}
                    onSave={save}
                    canSave={canSave}
                    isSaving={isSaving}
                    isPending={isPending}
                    placeholder="Add a sticky note for this form fill…"
                />
            }
            onDelete={remove}
            canEdit={canEdit}
            isPending={isPending}
            addLabel="+ Add sticky note"
        />
    );
};

export default StickyNotePanel;
